import React, { useEffect, useState, useRef } from "react";
import {
  useGetFolderIdQuery,
  useGetParentPurlFolderMutation,
  useCreatePurlFileExportMutation,
  useGetPurlFileExportsQuery,
  useLazyGetPurlFileExportDownloadQuery,
} from "../../services/api";
import FolderImg from "../../assets/images/FolderLogo.png";
import { HiOutlineDownload } from "react-icons/hi";
import Loader from "../Loader";
import { useAppSelector } from "../../store/store";
import { contactGroupPermission } from "../../services/contactGroupPermission";
import PurlDocumentTile from "./PurlDocumentTile";
import { tracker } from "../../services/mixpanel";
import { showNotification } from "../../services/showNotification";
import { config } from "../../configs";
import { useExport } from "../../providers/ExportProvider";
import { UserState } from "../../store/reducers/stageReducer";
import ConfirmationModal from "../../helpers/ConfirmationModal";

const PurlDocuments = ({
  stageData,
  openOverlay1,
  closeOverlay1,
}: {
  stageData: UserState;
  openOverlay1: (overlay: React.ReactElement) => void;
  closeOverlay1: () => void;
}) => {
  const permission = useAppSelector((state) => state.permission);

  const { data: folderData, isLoading: folderDataLoader } = useGetFolderIdQuery(
    undefined,
    {
      skip:
        !stageData.billOfSaleSignedDate &&
        !stageData.softCommitComplete &&
        !contactGroupPermission(permission, "showEmptyMyDocument"),
    }
  );

  const [getParentFolders, { isLoading }] = useGetParentPurlFolderMutation();
  const [rootFolders, setRootFolders] = useState<ParentFolderResponse[]>([]);
  const [createExport] = useCreatePurlFileExportMutation();
  const { data: exportsData, refetch: refetchExports } =
    useGetPurlFileExportsQuery(undefined);
  const [getExportDownload] = useLazyGetPurlFileExportDownloadQuery();
  const { jobs, addJob } = useExport();

  const [cachedExportForModal, setCachedExportForModal] = useState<any>(null);

  const showMyDocument =
    stageData.billOfSaleSignedDate ||
    rootFolders.length > 0 ||
    contactGroupPermission(permission, "showEmptyMyDocument");

  const fetchRootFolders = async () => {
    try {
      const res = await getParentFolders({}).unwrap();
      setRootFolders(res?.data || []);
    } catch (err) {
      console.error("Failed to load root folders", err);
    }
  };

  useEffect(() => {
    if (folderData?.data) {
      fetchRootFolders();
    }
  }, [folderData?.data]);

  const handleOpenFolder = (folder: ParentFolderResponse) => {
    tracker.dashboard.documentModal.onOpen(folder.name);
    openOverlay1(
      <PurlDocumentTile
        initialFolder={folder as unknown as Folder}
        props={{ type: folder.name }}
        onClose={closeOverlay1}
      />
    );
  };

  const handleDownloadFolder = async (
    e: React.MouseEvent,
    folder: ParentFolderResponse
  ) => {
    e.stopPropagation();

    if (jobs.find((j) => j.folderId === folder._id)) return;

    // Fetch the absolute latest export status dynamically from the backend before deciding
    const { data: freshData } = await refetchExports();

    // Check if we have a fresh cached export
    const cachedExport = freshData?.data?.find(
      (exp: any) => exp.folderName === folder.name && !exp.isOld && exp.s3Key
    );

    if (cachedExport) {
      setCachedExportForModal(cachedExport);
      return;
    }

    // No cached export found, start generation immediately without confirmation
    try {
      const res = await createExport({ folderName: folder.name }).unwrap();
      const jobId = res?.data?.jobId;

      if (!jobId) {
        showNotification("error", "Failed to queue export job");
        return;
      }

      addJob({ id: jobId, name: folder.name, folderId: folder._id });
      showNotification(
        "info",
        "Creating ZIP archive... We are packaging your folder in the background."
      );
    } catch (err) {
      console.error("Export error:", err);
      showNotification("error", "Failed to start export");
    }
  };

  const handleConfirmDownload = async () => {
    if (!cachedExportForModal) return;
    try {
      const downloadRes = await getExportDownload({
        id: cachedExportForModal._id,
      }).unwrap();
      if (downloadRes?.data?.url) {
        showNotification("success", "Export ready! Downloading...");
        window.open(downloadRes.data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to fetch download link", error);
      showNotification("error", "Failed to fetch download link");
    } finally {
      setCachedExportForModal(null);
    }
  };

  if (!showMyDocument && !isLoading) return null;

  return (
    <div className="bg-[#FEFCFB] rounded-xl w-full pt-1 h-[390px] shadow-lg">
      <h2 className="text-[25px] mb-[8px] p-1 text-black font-normal">
        My Documents
      </h2>

      {isLoading || folderDataLoader ? (
        <div className="mt-32">
          <Loader isLoading={true} size={20} />
        </div>
      ) : rootFolders.length === 0 ? (
        <div className="text-center mt-4 text-gray-800">Document not found</div>
      ) : (
        <div className="grid gap-y-3 grid-cols-3 h-[70%] border-t-4 border-[#EFEFF4] overflow-y-auto p-2 pt-4">
          {rootFolders.map((folder) => {
            const isExporting = jobs.some((j) => j.folderId === folder._id);

            return (
              <div
                key={folder._id}
                className="flex justify-center flex-col items-center cursor-pointer hover:opacity-80 transition p-2 relative"
              >
                <div className="w-full flex flex-col items-center">
                  <div
                    onClick={() => handleOpenFolder(folder)}
                    className="w-full flex flex-col items-center"
                  >
                    <img
                      className="h-[50px] mx-auto"
                      src={FolderImg}
                      alt="folder"
                    />
                    <div className="mt-4 max-w-full px-2">
                      <h3 className="text-center text-[13px] uppercase font-normal text-black break-words leading-tight px-1">
                        {!isExporting && (
                          <HiOutlineDownload
                            className="inline-block w-[18px] h-[18px] text-green-600 cursor-pointer hover:scale-110 hover:text-green-700 transition-all mr-1.5 relative -top-[1px]"
                            title="Download files"
                            onClick={(e) => handleDownloadFolder(e, folder)}
                          />
                        )}
                        {folder.name}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        show={!!cachedExportForModal}
        onHide={() => setCachedExportForModal(null)}
        onConfirm={handleConfirmDownload}
        title="Download Folder"
        message="No new changes since your last snapshot. Are you sure you want to download again?"
        confirmText="Yes, Download"
        cancelText="Cancel"
      />
    </div>
  );
};

export default PurlDocuments;
